import { useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { NAV_BAT, NAV_MAG } from '../../constants/navigation';
import { SUPPORT } from '../../constants/support';
import { C } from '../../theme/palette';
import { useModeConnexion } from '../../api/modeConnexion';

export function AidePage({ onNav, soc, estMixte, univers, setUnivers }) {
  const [ouvert, setOuvert] = useState(null);
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;
  const isMag = soc?.secteur === 'magasin';
  const GUIDE = [
    {
      id: 'demarrer',
      icon: '🚀',
      titre: 'Premiers pas',
      col: C.navy,
      items: [
        {
          q: 'Configurer ma société',
          r: [
            'Renseigne nom, adresse, n° TVA et IBAN dans Réglages.',
            'Importe ton logo : il apparaîtra sur les devis, factures et le QR-facture.',
            'En mode Mix, le Bâtiment et le Magasin sont deux sociétés séparées : remplis les coordonnées dans chacune.',
          ],
        },
        {
          q: "Comprendre l'accueil",
          r: [
            "Le tableau de bord montre l'encaissé, les impayés et les alertes.",
            'Les raccourcis mènent aux actions les plus fréquentes selon ton secteur.',
          ],
        },
        {
          q: 'Changer de module',
          r: [
            "Bâtiment, Magasin ou Mix : le module est fixé par l'abonnement. Plus → Abonnement → changer de module (patron) ; tes données sont conservées.",
            'En Mix, le bouton en haut à droite passe d’une société à l’autre (Bâtiment / Magasin).',
          ],
        },
      ],
    },
    {
      id: 'magasin',
      icon: '🛒',
      titre: 'Module Magasin',
      col: C.green,
      items: [
        {
          q: 'Gérer le catalogue et le stock',
          r: [
            'Ouvre Stock.',
            'Ajoute un article, ajuste les quantités, suis les alertes de stock bas.',
          ],
        },
        {
          q: 'Régler les remises par client',
          r: [
            "C'est le cœur du système.",
            'Chaque client peut avoir une remise globale et des remises par article.',
            "Elles s'appliquent automatiquement dans les ventes, devis et factures.",
          ],
        },
        {
          q: 'Faire une vente au comptoir',
          r: [
            'Ventes → + Nouvelle vente.',
            'Choisis le client, ajoute les articles : les remises se calculent seules.',
          ],
        },
      ],
    },
    {
      id: 'btp',
      icon: '🏗',
      titre: 'Module Bâtiment (BTP)',
      col: C.teal,
      items: [
        {
          q: 'Créer un devis',
          r: [
            'Devis → modèle prêt ou devis vierge.',
            "Ajoute les lignes (catalogue de prestations ou saisie libre), l'acompte et la TVA.",
          ],
        },
        {
          q: 'Envoyer et faire signer',
          r: [
            'Envoie par e-mail ou WhatsApp.',
            "Le client signe électroniquement via le QR ; un chantier et une facture d'acompte sont créés.",
          ],
        },
        {
          q: 'Suivre un chantier',
          r: [
            'Onglets : résumé, portail client, avenants, réception, bons, sous-traitants, factures, heures, photos, plans, messages.',
          ],
        },
        {
          q: "L'espace employé",
          r: [
            codeOuvrier
              ? "À la connexion, onglet « Code ouvrier » : code d'entreprise (Réglages → Accès), puis le code PIN de l'employé."
              : "L'employé reçoit son accès par l'invitation envoyée depuis sa fiche (Équipe → « Inviter ») : il choisit son adresse e-mail et son mot de passe. À chaque connexion, un code à 6 chiffres lui est ensuite envoyé par e-mail ou par SMS.",
            "L'employé pointe ses heures, voit son chantier, envoie photos et rapports.",
          ],
        },
      ],
    },
    {
      id: 'communs',
      icon: '🧩',
      titre: 'Fonctions communes',
      col: C.purple,
      items: [
        {
          q: 'Factures & QR-Bill',
          r: [
            'Numérotation continue, QR-Bill suisse (SIX) scannable.',
            'Acompte, solde, avoir et rectification gérés.',
          ],
        },
        {
          q: 'Changer le taux de TVA',
          r: ['Sur chaque devis/facture : 8.1% (normal), 2.6% (réduit), 3.8% (hébergement) ou 0%.'],
        },
        {
          q: 'Comptabilité & TVA',
          r: [
            'Espace comptable : CA, marges par chantier, décompte TVA, échéancier, export fiduciaire.',
          ],
        },
        {
          q: 'Document A4 imprimable',
          r: [
            'Depuis tout devis/facture : « Voir le document A4 ».',
            '6 styles de présentation, logo, IBAN, mentions, TVA, QR-Bill.',
          ],
        },
      ],
    },
  ];
  const ordre = isMag
    ? ['demarrer', 'magasin', 'btp', 'communs']
    : ['demarrer', 'btp', 'magasin', 'communs'];
  const sections = ordre.map((id) => GUIDE.find((g) => g.id === id)).filter(Boolean);
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      navItems={isMag ? NAV_MAG : NAV_BAT}
      back
      onBack={() => onNav('plus')}
      pageTitle="❓ Aide & guide"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <Card style={{ marginBottom: 11 }}>
        <div
          style={{
            background: 'linear-gradient(135deg,' + C.navy + ',' + C.navyM + ')',
            padding: '16px 16px',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 900, color: '#fff', marginBottom: 3 }}>
            📖 Comment utiliser SwissTech
          </div>
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,.7)', lineHeight: 1.5 }}>
            Touche une question pour voir les étapes.
          </div>
        </div>
      </Card>
      {sections.map((sec) => (
        <Card key={sec.id} style={{ marginBottom: 11 }}>
          <div
            style={{
              padding: '12px 14px',
              borderLeft: '4px solid ' + sec.col,
              display: 'flex',
              gap: 9,
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 18 }}>{sec.icon}</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{sec.titre}</span>
          </div>
          <div style={{ padding: '0 14px 8px' }}>
            {sec.items.map((it, idx) => {
              const key = sec.id + idx;
              const open = ouvert === key;
              return (
                <div key={key} style={{ borderBottom: '1px solid ' + C.border }}>
                  <div
                    onClick={() => setOuvert(open ? null : key)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '11px 0',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: C.ink,
                        flex: 1,
                        marginRight: 8,
                      }}
                    >
                      {it.q}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: C.muted,
                        transform: open ? 'rotate(90deg)' : 'none',
                      }}
                    >
                      ›
                    </span>
                  </div>
                  {open && (
                    <div style={{ padding: '2px 0 12px' }}>
                      {it.r.map((etape, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <span
                            style={{
                              flexShrink: 0,
                              width: 18,
                              height: 18,
                              borderRadius: 9,
                              background: sec.col + '18',
                              color: sec.col,
                              fontSize: 9,
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {i + 1}
                          </span>
                          <span style={{ fontSize: 9.5, color: C.grey, lineHeight: 1.5 }}>
                            {etape}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
      <Card style={{ marginBottom: 11 }}>
        <Bar icon="💬" title="Nous contacter" color={C.navy} />
        <div style={{ padding: 13 }}>
          {[
            ['📧', 'Email', SUPPORT.email],
            ['📞', 'Téléphone', SUPPORT.telephone],
            ['🕐', 'Horaires', SUPPORT.horaires],
          ]
            .filter(([, , v]) => v)
            .map(([ic, k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '7px 0',
                  borderBottom: '1px solid ' + C.border,
                }}
              >
                <span style={{ fontSize: 10, color: C.grey }}>
                  {ic} {k}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.ink }}>{v}</span>
              </div>
            ))}
        </div>
      </Card>
    </Shell>
  );
}
